import { useEffect, useSyncExternalStore } from 'react';
import { getSessionToken } from '../safeStorage';

// The student's colours and companion are chosen before sign-up and stay fixed through the starting assessment, so the
// same companion walks with them from the first question. Once the assessment is done they can change them (the colour
// button in the header unlocks). A visitor who is not signed in is never locked.
//
// "Assessment done" is read from the student's own guided path (the diagnostic step), once per sign-in. If that cannot
// be read, the lock opens rather than trapping someone with a look they cannot change.

let state = { token: null, known: false, done: false };
let pending = null;
const subs = new Set();
const emit = (next) => { state = { ...state, ...next }; subs.forEach((f) => f()); };
const subscribe = (f) => { subs.add(f); return () => { subs.delete(f); }; };
const snapshot = () => state;

function load(token) {
  if (pending === token) return;
  pending = token;
  emit({ token, known: false, done: false });
  Promise.resolve()
    .then(() => fetch('/api/sat?action=guided-path', { headers: { Authorization: `Bearer ${token}` } }))
    .then((r) => (r && r.ok ? r.json() : null))
    .then((d) => {
      if (pending !== token) return;
      const steps = d && d.guided && Array.isArray(d.guided.steps) ? d.guided.steps : null;
      const diagnostic = steps ? steps.find((s) => s.id === 'diagnostic') : null;
      emit({ known: true, done: diagnostic ? !!diagnostic.done : true });
    })
    .catch(() => { if (pending === token) emit({ known: true, done: true }); });
}

// Called the moment a graded assessment result is on screen, so the lock opens without a reload.
export function markAssessmentDone() {
  const token = getSessionToken();
  if (!token) return;
  pending = token;
  emit({ token, known: true, done: true });
}

// Called on sign-out so the next person to sign in on this device is read afresh.
export function resetAssessmentGate() {
  pending = null;
  emit({ token: null, known: false, done: false });
}

export function useAppearanceLock(signedIn) {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  const token = signedIn ? getSessionToken() : null;
  useEffect(() => { if (token && s.token !== token) load(token); }, [token, s.token]);
  if (!token) return { locked: false, known: true };
  const mine = s.token === token && s.known;
  return { locked: !(mine && s.done), known: mine };
}
